import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gem, Loader2, KeyRound } from "lucide-react";

export function LoginView() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/studio/projects");
    else setError(res.error);
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0a0c10] px-4">
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 20%, rgba(245,158,11,0.14), transparent 45%), radial-gradient(circle at 85% 80%, rgba(56,189,248,0.08), transparent 40%)",
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-amber-500/15 ring-1 ring-amber-500/30">
            <Gem className="h-7 w-7 text-amber-400" />
          </div>
          <h1 className="font-serif text-4xl tracking-tight text-slate-100">Rik&apos;s Audiobook Studio</h1>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.3em] text-amber-500/70">
            Audiobook Producer
          </p>
        </div>

        <div className="rounded-xl border border-amber-500/15 bg-[#12161f]/90 p-8 shadow-2xl backdrop-blur">
          <h2 className="mb-1 font-serif text-2xl text-slate-100">Enter the studio</h2>
          <p className="mb-6 text-sm text-slate-400">Private workstation. Authorized producer access only.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-400">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@housediamond.studio"
                required
                className="bg-slate-950/60 text-slate-100"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-400">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                required
                className="bg-slate-950/60 text-slate-100"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-400">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full gap-2 bg-amber-500 font-medium text-slate-950 transition-colors hover:bg-amber-400"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              {loading ? "Authenticating…" : "Open workstation"}
            </Button>
          </form>
        </div>
        <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
          Phase 1 · Foundation
        </p>
      </motion.div>
    </div>
  );
}
